import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getAdminCounters, type CrmErpCounters } from "@/services/adminService";

interface AdminCountersValue {
  counters: CrmErpCounters;
  /** Re-fetch the live counts (call after any CRM/ERP mutation). */
  refresh: () => void;
}

const EMPTY: CrmErpCounters = { openDeals: 0, unpaidInvoices: 0 };

const AdminCountersContext = createContext<AdminCountersValue>({
  counters: EMPTY,
  refresh: () => {},
});

/**
 * Provides live CRM/ERP counters (open deals, unpaid invoices) to the admin
 * shell so the sidebar can render badges. Sections call `refresh()` after they
 * create or change data, keeping the badges in sync without a full reload.
 */
export function AdminCountersProvider({ children }: { children: ReactNode }) {
  const { accessToken } = useAuth();
  const [counters, setCounters] = useState<CrmErpCounters>(EMPTY);

  const refresh = useCallback(async () => {
    try {
      const next = await getAdminCounters(accessToken);
      setCounters(next);
    } catch {
      // Leave the last known counts — badges simply stay put on failure.
    }
  }, [accessToken]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <AdminCountersContext.Provider value={{ counters, refresh }}>
      {children}
    </AdminCountersContext.Provider>
  );
}

export function useAdminCounters(): AdminCountersValue {
  return useContext(AdminCountersContext);
}